import { Link } from 'react-router-dom';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { OG_IMAGES } from '@/constants/seoImages';
import { Button } from '@/components/ui/button';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import {
  ChatBubbleLeftRightIcon,
  HeartIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
} from '@heroicons/react/24/outline';

const ConversationGuide = () => {
  const { openModal } = useEarlyAdopter();

  const conversationTopics = [
    {
      icon: ShieldCheckIcon,
      title: 'Home Safety',
      opening: '"I noticed the rug in the hallway seems a little loose. Would you be open to checking a few rooms together, just to make sure everything is set up the way you want it?"',
      tip: 'Frame it as a project you do together, not something you do to them.',
    },
    {
      icon: HeartIcon,
      title: 'Health Tracking',
      opening: '"Your doctor mentioned keeping an eye on your blood pressure. What if there was an easy way to track it from home, so you have the numbers ready for your next visit?"',
      tip: 'Connect it to something they already care about, their doctor, their independence.',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Staying Connected',
      opening: '"I worry sometimes, and I know my calls can feel like check-ups. What if there was a way for me to see that you\'re doing well, without me having to ask every day?"',
      tip: 'Be honest about your own feelings. This isn\'t about control, it\'s about your relationship.',
    },
    {
      icon: LightBulbIcon,
      title: 'Getting Started',
      opening: '"I found something called SeniorThrive that a lot of people your age are using. It\'s not a medical thing, it\'s more like a daily companion for staying independent. Would you want to look at it together?"',
      tip: 'Offer to explore it together. Don\'t present it as something you\'ve already decided.',
    },
  ];

  return (
    <>
      <EnhancedSEO
        title="How to Talk to Your Parent About SeniorThrive"
        description="Practical conversation starters and guidance for adult children who want to introduce SeniorThrive to an aging parent, with empathy, respect, and confidence."
        canonical="https://seniorthrive.com/conversation-guide"
        image={OG_IMAGES.conversationGuide}
        breadcrumbs={[
          { name: 'Home', url: 'https://seniorthrive.com/' },
          { name: 'Family', url: 'https://seniorthrive.com/thrivefamily' },
          { name: 'Conversation Guide', url: 'https://seniorthrive.com/conversation-guide' },
        ]}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary/5 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-primary font-bold mb-4 uppercase tracking-wider">
              For Family Members
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              How to Talk to Your Parent About Safety at Home
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              You care deeply. They value their independence. These conversations
              don't have to be hard, they just need the right approach.
            </p>
          </div>
        </div>
      </section>

      {/* The Real Challenge */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-destructive/5 rounded-md p-8 shadow-raised">
                <div className="flex items-center gap-3 mb-4">
                  <ExclamationTriangleIcon className="w-6 h-6 text-destructive" />
                  <h3 className="text-xl font-bold text-foreground">What doesn't work</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• "I've been worried about you" (feels like you think they're declining)</li>
                  <li>• "We need to talk about your safety" (sounds like an intervention)</li>
                  <li>• Bringing it up after a scare (feels reactive and pressured)</li>
                  <li>• Making the decision for them (removes their autonomy)</li>
                </ul>
              </div>
              <div className="rounded-md p-8 shadow-raised">
                <div className="flex items-center gap-3 mb-4">
                  <HandRaisedIcon className="w-6 h-6 text-primary" />
                  <h3 className="text-xl font-bold text-foreground">What works better</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• Start when things are calm, not after a crisis</li>
                  <li>• Frame it as something you explore together</li>
                  <li>• Lead with their goals, not your fears</li>
                  <li>• Let them decide the pace, offer, don't push</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversation Starters */}
      <section className="py-16 lg:py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Conversation Starters That Actually Work
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Pick the topic that feels most natural. You don't have to cover everything at once.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {conversationTopics.map((topic, i) => (
                <div key={i} className="bg-card rounded-md p-8 shadow-raised hover:-translate-y-0.5 hover:shadow-floating transition-all">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center">
                      <topic.icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground">{topic.title}</h3>
                  </div>
                  <div className="bg-muted/50 rounded-md p-4 mb-4 shadow-raised">
                    <p className="text-foreground italic leading-relaxed">
                      {topic.opening}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <LightBulbIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                    <p className="text-muted-foreground leading-relaxed">
                      <span className="font-bold text-foreground">Tip:</span> {topic.tip}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Principles */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-12">
              Three Principles for Every Conversation
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: 'Respect their autonomy',
                  description: 'They have lived a full life making their own decisions. Your role is to offer, not to override. SeniorThrive is designed so they stay in control of what is shared and what stays private.',
                },
                {
                  title: 'Lead with their goals',
                  description: 'Ask what they want. Staying home? Seeing the grandkids more? Feeling confident alone? Connect SeniorThrive to what matters to them, not what worries you.',
                },
                {
                  title: 'Be patient',
                  description: 'They may not say yes today. That is okay. Plant the seed, step back, and revisit when the moment is right. The best conversations happen over time, not in one sitting.',
                },
              ].map((principle, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-lg">{i + 1}</span>
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-3">{principle.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Ready When They Are
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8">
              When the time is right, SeniorThrive makes it easy to get started together.
              Family members can explore the platform free, billing only begins when your
              loved one joins and activates their account.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="text-lg px-8 py-6"
                onClick={() => openModal('Conversation Guide - Get Early Access')}
              >
                Get Early Access
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-6" asChild>
                <Link to="/thrivefamily">Learn About the Family Plan</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ConversationGuide;
